<template>
  <validation-observer v-if="dialogComputed" ref="observer" v-slot="{ invalid }">
    <v-dialog v-model="dialogComputed" max-width="400px" scrollable :persistent="!isInquiry">
      <v-card>
        <v-card-title class="primary white--text py-2">
          {{ setting.title }}
          <v-spacer />
          <Btn icon="mdi-close" @click="close" depressed iconProp color="white" />
        </v-card-title>
        <v-card-text>
          <v-container fluid class="px-0 py-2">
            <v-row>
              <v-col>
                <TextFieldRef v-if="isInquiry || isEdit" :label="$t('ELE0044')" :value="userCdComputed" />
                <TextFieldInput
                  v-else
                  :label="$t('ELE0044')"
                  v-model="userCdComputed"
                  :maxLength="20"
                  validationMode="aggressive"
                  :debounce="500"
                  rules="alphaNum|userCdUnique"
                  required
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <TextFieldRef v-if="isInquiry" :label="$t('ELE0045')" :value="userNameComputed" />
                <TextFieldInput v-else :label="$t('ELE0045')" v-model="userNameComputed" :maxLength="40" required />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <CompanyInput v-if="isInquiry" v-model="companyCdComputed" disabled />
                <CompanyInput v-else v-model="companyCdComputed" required validOnly />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <TextFieldRef v-if="isInquiry" v-model="mailComputed" :label="$t('ELE0047')" />
                <TextFieldInput
                  v-else
                  v-model="mailComputed"
                  :label="$t('ELE0047')"
                  :maxLength="60"
                  validationMode="aggressive"
                  :debounce="500"
                  :rules="mailRules"
                  required
                />
              </v-col>
            </v-row>
            <v-row dense>
              <v-col>
                <Checkbox v-if="isInquiry" v-model="isReceivedMailComputed" :label="$t('ELE0340')" disabled />
                <Checkbox v-else v-model="isReceivedMailComputed" :label="$t('ELE0340')" />
              </v-col>
            </v-row>
            <v-row dense>
              <v-col>
                <RoleInput v-if="isInquiry" v-model="userRoleCdsComputed" validOnly disabled />
                <RoleInput v-else v-model="userRoleCdsComputed" validOnly required />
              </v-col>
            </v-row>
            <v-row v-if="isInquiry || isEdit">
              <v-col>
                <BtnChooseValid v-model="isValidComputed" :disabled="isInquiry" />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <span class="d-flex flex-row space-between">
            <BtnClose v-if="isInquiry" @click="close" />
            <BtnSave v-else @click="onClickSave" :disabled="invalid" />
          </span>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </validation-observer>
</template>

<script>
import { API } from '@/plugins/api'
import { ModalMode } from '@/enum'
import { ValidationObserver } from 'vee-validate'
import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'

import Btn from '@/components/atoms/base/button/Btn'
import TextFieldInput from '@/components/atoms/base/text-field/TextFieldInput'
import TextFieldRef from '@/components/atoms/base/text-field/TextFieldRef'
import BtnSave from '@/components/atoms/button/BtnSave'
import BtnClose from '@/components/atoms/button/BtnClose'
import BtnChooseValid from '@/components/atoms/button/BtnChooseValid'
import Checkbox from '@/components/atoms/base/checkbox/Checkbox'
import CompanyInput from '@/components/atoms/select/CompanyInput'
import RoleInput from '@/components/atoms/select/RoleInput'

export default {
  name: 'UserDialog',

  mixins: [APIUtils, Message, Loading],

  components: {
    ValidationObserver,
    Btn,
    BtnSave,
    BtnClose,
    BtnChooseValid,
    TextFieldInput,
    TextFieldRef,
    Checkbox,
    CompanyInput,
    RoleInput,
  },

  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    mode: {
      type: String,
      required: true,
    },
    userCd: {
      type: String,
      required: true,
    },
    userName: {
      type: String,
      required: true,
    },
    mail: {
      type: String,
      required: false,
      default: '',
    },
    companyCd: {
      type: String,
      required: true,
    },
    companyName: {
      type: String,
      required: true,
    },
    userRoleCds: {
      type: Array,
      required: true,
      default: () => [],
    },
    isReceivedMail: {
      type: Boolean,
      required: true,
      default: false,
    },
    isValid: {
      type: Boolean,
      required: true,
    },
    versionNo: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      settings: [
        {
          mode: ModalMode.ADD,
          title: this.$t('ELE0016'),
          url: '/master/users/register',
        },
        {
          mode: ModalMode.EDIT,
          title: this.$t('ELE0017'),
          url: '/master/users/{id}/update',
        },
        {
          mode: ModalMode.INQUIRY,
          title: this.$t('ELE0018'),
          url: '',
        },
      ],
    }
  },

  computed: {
    dialogComputed: {
      get() {
        return this.dialog
      },
      set(value) {
        this.$emit('update:dialog', value)
      },
    },
    userCdComputed: {
      get() {
        return this.userCd
      },
      set(value) {
        if (value) {
          value = value.trim()
        }
        this.$emit('update:userCd', value)
      },
    },
    userNameComputed: {
      get() {
        return this.userName
      },
      set(value) {
        this.$emit('update:userName', value)
      },
    },
    mailComputed: {
      get() {
        return this.mail
      },
      set(value) {
        if (value) {
          value = value.trim()
        }
        this.$emit('update:mail', value)
      },
    },
    companyCdComputed: {
      get() {
        return this.companyCd
      },
      set(value) {
        this.$emit('update:companyCd', value)
      },
    },
    companyNameComputed: {
      get() {
        return this.companyName
      },
      set(value) {
        this.$emit('update:companyName', value)
      },
    },
    isReceivedMailComputed: {
      get() {
        return this.isReceivedMail
      },
      set(value) {
        this.$emit('update:isReceivedMail', value)
      },
    },
    isValidComputed: {
      get() {
        return this.isValid
      },
      set(value) {
        this.$emit('update:isValid', value)
      },
    },
    versionNoComputed: {
      get() {
        return this.versionNo
      },
    },
    userRoleCdsComputed: {
      get() {
        return this.userRoleCds
      },
      set(value) {
        this.$emit('update:userRoleCds', value)
      },
    },
    setting: {
      get() {
        return this.settings.find((el) => el.mode === this.mode)
      },
    },
    isEdit: {
      get() {
        return this.setting.mode === ModalMode.EDIT
      },
    },
    isInquiry: {
      get() {
        return this.setting.mode === ModalMode.INQUIRY
      },
    },
    mailRules: {
      get() {
        let rules = 'halfWidth|'
        if (this.isReceivedMail) {
          rules = rules + 'email|'
        }
        if (this.isEdit) {
          rules = rules + `mailUnique:${this.userCdComputed}`
        } else {
          rules = rules + `mailUnique`
        }
        return rules
      },
    },
  },

  methods: {
    close() {
      this.dialogComputed = false
    },
    async onClickSave() {
      try {
        if (!(await this.$refs.observer.validate())) {
          return
        }
        this.startLoading()
        const body = {
          userCd: this.userCdComputed,
          userName: this.userNameComputed,
          companyCd: this.companyCdComputed,
          mail: this.mailComputed,
          userRoleCds: this.userRoleCdsComputed,
          isReceivedMail: this.isReceivedMailComputed,
          isValid: this.isValidComputed,
          versionNo: this.versionNoComputed,
        }
        const url = this.setting.url.replace('{id}', this.userCdComputed)
        await API.post(url, body)

        this.setSuccessMessage(this.$t('MSG0011', { p0: this.$t('ELE0058') }))
        this.close()
        this.$emit('change')
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0003', { p0: this.$t('ELE0058') })))
      } finally {
        this.stopLoading()
      }
    },
  },
}
</script>
